import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRight, ArrowDown } from 'lucide-react';
import { MARKETS, MARKET_STYLES } from '../../data/whatWeDo';

// Representative project photos shown in a strip beneath each pillar card.
// (Placeholders drawn from existing OMC project photos until client's
// Dropbox photos are supplied.)
const MARKET_PHOTOS = {
  water: [
    { src: '/capabilities/treatment-hero.jpg', loc: 'Banks County, GA' },
    { src: '/capabilities/pumps-hero.jpg', loc: 'Lexington-Leesville, SC' },
    { src: '/capabilities/underground-alt.jpg', loc: 'Eunice, LA' },
  ],
  industrial: [
    { src: '/capabilities/vaults-hero.jpg', loc: 'Bullard, TX' },
    { src: '/capabilities/piping-alt.jpg', loc: 'Laurens, SC' },
    { src: '/capabilities/site-i-spheres.jpg' },
  ],
  energy: [
    { src: '/capabilities/startup-hero.jpg', loc: 'Summerton, SC' },
    { src: '/capabilities/site-w-aerial-foundation.jpg' },
    { src: '/capabilities/piping-hero.jpg', loc: 'Gaffney, SC' },
  ],
};

// Step 1 of the "What We Do" experience: three large interactive market
// pillars. Clicking one stretches the capabilities section open below.
export default function MarketPillars({ onSelect, activeMarket }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.08 });

  return (
    <section
      id="markets"
      ref={ref}
      className="relative pt-20 pb-6 sm:pt-24 sm:pb-7 lg:pt-28 lg:pb-8 bg-background overflow-hidden border-t border-border"
      data-testid="markets-section"
    >
      <div className="absolute inset-0 bg-grain opacity-[0.03] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-6xl mb-3 lg:mb-4"
        >
          <p className="kicker mb-3" data-testid="markets-kicker">Where We Work</p>
          <h2
            className="editorial-headline font-bold text-6xl sm:text-7xl lg:text-[6.5rem] xl:text-[7.5rem] text-foreground mb-3 leading-[0.95]"
            data-testid="markets-headline"
          >
            Three Pillars.
            <span className="italic font-bold text-forest"> One </span>
            Standard.
          </h2>
          <p className="text-foreground/70 leading-[1.6] text-base sm:text-lg max-w-2xl">
            Explore our capabilities by selecting one of the pillars below.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
          {MARKETS.map((m, i) => {
            const s = MARKET_STYLES[m.id];
            const Icon = m.icon;
            const isActive = activeMarket === m.id;
            return (
              <div key={m.id} className="flex flex-col items-center">
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={inView ? { opacity: 1 } : {}}
                  transition={{ delay: 0.2 + i * 0.12, duration: 0.5 }}
                  className="mb-4 inline-flex"
                  aria-hidden="true"
                >
                  <motion.span
                    animate={{ y: [0, 9, 0] }}
                    transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut', delay: i * 0.18 }}
                    className="inline-flex"
                  >
                    <ArrowDown className="w-8 h-8 text-forest" strokeWidth={2} />
                  </motion.span>
                </motion.span>
                <motion.button
                  type="button"
                  onClick={() => onSelect(m.id)}
                  initial={{ opacity: 0, y: 24 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className={`group relative w-full aspect-[4/5] rounded-2xl overflow-hidden text-left shadow-elev-2 border transition-[transform,box-shadow,border-color] duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--forest))] focus-visible:ring-offset-2 ${
                  isActive
                    ? 'border-[hsl(var(--forest))] ring-2 ring-[hsl(var(--forest))] ring-offset-2 -translate-y-1.5'
                    : 'border-border hover:-translate-y-1.5 hover:shadow-elev-2'
                }`}
                data-testid={`market-pillar-${m.id}`}
              >
                <img
                  src={m.image}
                  alt={m.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--coal))] via-[hsl(var(--coal)/0.55)] to-[hsl(var(--coal)/0.05)]" />
                <div className={`absolute top-0 left-0 right-0 h-1.5 ${s.rule}`} />
                {isActive && (
                  <motion.div
                    initial={{ opacity: 0, scaleX: 0.4 }}
                    animate={{ opacity: 1, scaleX: 1 }}
                    transition={{ duration: 0.35 }}
                    className={`absolute bottom-0 left-0 right-0 h-1.5 ${s.rule}`}
                  />
                )}

                <div className="absolute top-6 left-6 flex items-center">
                  <span className={`w-14 h-14 rounded-xl flex items-center justify-center border border-white/30 shadow-lg ${s.rule}`}>
                    <Icon className="w-7 h-7 text-white" strokeWidth={2.5} />
                  </span>
                </div>

                <div className="absolute inset-x-0 bottom-0 p-6 lg:p-8 text-center">
                  <h3 className="font-display font-light text-3xl lg:text-4xl text-white leading-snug tracking-wide mb-3">
                    {m.title}
                  </h3>
                  <p className="font-display italic text-base text-white/75 mb-5">{m.headline}</p>
                  <span className="inline-flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-white">
                    {isActive ? 'Capabilities Below' : 'View Capabilities'}
                    <ArrowRight
                      className={`w-4 h-4 transition-transform duration-300 ${
                        isActive ? 'rotate-90' : 'group-hover:translate-x-1'
                      }`}
                    />
                  </span>
                </div>
              </motion.button>

              {/* Project photo strip beneath each pillar — hidden once a pillar is selected */}
              <AnimatePresence initial={false}>
                {activeMarket == null && (
                  <motion.div
                    key={`strip-${m.id}`}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.35, ease: [0.22, 0.61, 0.36, 1] }}
                    className="w-full overflow-hidden"
                    data-testid={`pillar-photos-${m.id}`}
                  >
                    <div className="mt-3 grid grid-cols-3 gap-2 w-full">
                      {MARKET_PHOTOS[m.id].map((p) => (
                        <div key={p.src} className="relative aspect-[4/3] overflow-hidden rounded-lg border border-border bg-bone-2">
                          <img
                            src={p.src}
                            alt={`${m.label} project${p.loc ? ' — ' + p.loc : ''}`}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                          />
                          {p.loc && (
                            <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent px-1.5 pt-4 pb-1">
                              <span className="block truncate font-mono text-[8.5px] tracking-[0.08em] uppercase text-white/90">{p.loc}</span>
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
